import React, { useState } from 'react';
import { Mail, Link, CheckCircle, AlertTriangle, Settings } from 'lucide-react';
import { onboardingIntegrationClient } from '../../../api/onboardingIntegrationClient';

interface SimpleEmailWebhookProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const SimpleEmailWebhook: React.FC<SimpleEmailWebhookProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [webhooks] = useState([
    {
      id: 'wh-1',
      from: 'client@example.com',
      subject: 'Re: Tyler ERP Setup Documentation',
      status: 'processed',
      mappedTo: 'Task Comment',
      timestamp: '2024-08-24T14:30:00Z'
    },
    {
      id: 'wh-2',
      from: 'pm@tyler.com',
      subject: 'Data Migration Status Update',
      status: 'processed',
      mappedTo: 'Task Update',
      timestamp: '2024-08-24T16:15:00Z'
    }
  ]);

  const [testResult, setTestResult] = useState<string>('');

  const testWebhook = async () => {
    try {
      await onboardingIntegrationClient.testWebhook();
      setTestResult('Webhook test successful!');
    } catch (error) {
      setTestResult('Webhook test failed');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'text-[#8A9150] bg-[#8A9150]/10 border-[#8A9150]/20';
      case 'failed': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-yellow-600 bg-yellow-100 border-yellow-200';
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Mail className="w-5 h-5 text-[#4267A1] mr-2" />
        Email-to-Task Integration
      </h3>
      
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Clients reply to task notification emails</li>
            <li>• Emails are automatically mapped to project tasks</li>
            <li>• Comments are added to the task timeline</li>
            <li>• Project team is notified of client responses</li>
          </ul>
        </div>
        
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Recent Email Processing</h4>
          <div className="space-y-2">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-4 h-4 text-gray-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{webhook.subject}</p>
                  <p className="text-xs text-gray-500">From: {webhook.from}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Link className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-600">{webhook.mappedTo}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(webhook.status)}`}>
                  {webhook.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {userRole !== 'client' && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={testWebhook}
                  className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium"
                >
                  Test Webhook
                </button>
                
                {testResult && (
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-[#8A9150]" />
                    <span className="text-sm text-[#8A9150]">{testResult}</span>
                  </div>
                )}
              </div>
              
              <button className="flex items-center space-x-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <Settings className="w-4 h-4" />
                <span>Configure</span>
              </button>
            </div>
          </div>
        )}
        
        <div className="text-xs text-gray-500">
          <p>• Email processing uses existing InboundWebhookBee infrastructure</p>
          <p>• All email-to-task mappings are logged for audit purposes</p>
          <p>• Supports pattern matching for automatic task assignment</p>
        </div>
      </div>
    </div>
  );
};
